import { BottomSheetScrollView } from '@gorhom/bottom-sheet'
import { Pressable, StyleSheet, View, useWindowDimensions } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { Grabber } from '../components/Grabber'
import { ScrollFade, useScrollEdgeFades } from '../components/ScrollFade'
import type { WithdrawalSourceTokenOption } from '../flow/withdrawalFlowConfig'
import { FigmaSuccessCheckIcon } from '../icons/FigmaSystemIcons'
import { TokenChainIcon } from '../icons/TokenChainIcon'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'

export interface SelectSourceTokenScreenProps {
  options: WithdrawalSourceTokenOption[]
  /** Index into `options` — two options can share a symbol (perps vs spot). */
  selectedIndex: number
  onSelect: (index: number) => void
  onBack: () => void
}

/**
 * Pick which balance the withdrawal comes out of. Flat, not grouped by chain:
 * the sources a host offers are balances on one venue, and two of them can
 * share a symbol (Lighter's perps and spot USDC), so the row identity is the
 * option itself rather than its symbol.
 */
export function SelectSourceTokenScreen({
  options,
  selectedIndex,
  onSelect,
  onBack,
}: SelectSourceTokenScreenProps) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  const { height } = useWindowDimensions()
  const insets = useSafeAreaInsets()
  const { showTopFade, showBottomFade, scrollProps } = useScrollEdgeFades()

  return (
    // A full window tall, like the step it replaces — the flow sheet is pinned
    // to a fixed detent while a crypto withdrawal step is up (see sheetSizing).
    <Box style={{ height, paddingTop: insets.top }}>
      <Grabber />
      <Box
        flexDirection="row"
        alignItems="center"
        style={[styles.topBar, { height: theme.sizes.topBar }]}
      >
        <Pressable
          onPress={onBack}
          accessibilityRole="button"
          accessibilityLabel={t('common.back')}
          hitSlop={8}
        >
          <Text variant="bodyMedium">{t('common.back')}</Text>
        </Pressable>
        <Text variant="headerTitle" style={styles.title}>
          {t('withdrawCrypto.selectSource')}
        </Text>
        <Box style={styles.spacer} />
      </Box>

      <View style={styles.scrollArea}>
        <BottomSheetScrollView
          style={styles.scroll}
          contentContainerStyle={[
            styles.list,
            { paddingBottom: LIST_BOTTOM_GAP + insets.bottom },
          ]}
          showsVerticalScrollIndicator={false}
          {...scrollProps}
        >
          {options.map((option, index) => {
            const label = option.label ?? option.token.symbol
            const isSelected = index === selectedIndex
            return (
              <Pressable
                // Identity is positional — two options can be the same token
                // with the same label and differ only in which balance they
                // draw from, so the index is the only unique part of the key.
                key={`${index}:${option.token.chainId}:${option.token.address}`}
                onPress={() => onSelect(index)}
                accessibilityRole="button"
                accessibilityLabel={label}
                accessibilityState={{ selected: isSelected }}
              >
                <Box
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="space-between"
                  borderRadius="element"
                  style={[
                    styles.row,
                    isSelected
                      ? { backgroundColor: theme.background.secondary }
                      : undefined,
                  ]}
                >
                  <Box
                    flexDirection="row"
                    alignItems="center"
                    style={styles.token}
                  >
                    <TokenChainIcon
                      symbol={option.token.symbol}
                      iconSrc={option.iconSrc}
                    />
                    <Text
                      variant="bodyMedium"
                      style={{ color: theme.content.elements.primary }}
                    >
                      {label}
                    </Text>
                  </Box>
                  {isSelected ? <FigmaSuccessCheckIcon size={16} /> : null}
                </Box>
              </Pressable>
            )
          })}
        </BottomSheetScrollView>
        {showTopFade ? (
          <ScrollFade
            edge="top"
            color={theme.background.modal}
            id="source-token-list"
          />
        ) : null}
        {showBottomFade ? (
          <ScrollFade
            edge="bottom"
            color={theme.background.modal}
            id="source-token-list"
          />
        ) : null}
      </View>
    </Box>
  )
}

/** Keeps the last row clear of the sheet edge. */
const LIST_BOTTOM_GAP = 24

const styles = StyleSheet.create({
  topBar: { paddingHorizontal: 16, gap: 12 },
  title: { flex: 1 },
  spacer: { width: 20 },
  scrollArea: { flex: 1, position: 'relative' },
  scroll: { flex: 1 },
  list: { paddingHorizontal: 16, gap: 4 },
  row: { paddingHorizontal: 12, paddingVertical: 10 },
  token: { gap: 10 },
})
